import { useState, useEffect, useCallback } from 'react';
import { candidateCacheService, CandidateData, CacheMetrics } from '../services/candidateCache';
import { useAuth } from '../context/AuthContext';

interface UseSupabaseCandidateCacheOptions {
  cacheKey?: string;
  ttlMinutes?: number;
  enableBackgroundRefresh?: boolean;
  enableRealtime?: boolean;
  autoMigrate?: boolean;
}

interface UseSupabaseCandidateCacheReturn {
  candidates: CandidateData[];
  isLoading: boolean;
  isFromCache: boolean;
  error: string | null;
  refreshCache: () => Promise<void>;
  clearCache: () => Promise<void>;
  setCandidates: (candidates: CandidateData[]) => Promise<void>;
  invalidatePattern: (pattern: string) => Promise<number>;
  getCacheMetrics: () => Promise<CacheMetrics | null>;
  getCacheSize: () => Promise<{ totalEntries: number; totalSizeKB: number }>;
  migrateLegacyData: () => Promise<{ migrated: number; errors: string[] }>;
}

const DEFAULT_TTL_MINUTES = 15;
const DEFAULT_CACHE_KEY = 'candidates_cache';

export function useSupabaseCandidateCache(
  fetchFunction: () => Promise<CandidateData[]>,
  options: UseSupabaseCandidateCacheOptions = {}
): UseSupabaseCandidateCacheReturn {
  const { user } = useAuth();
  const {
    cacheKey = DEFAULT_CACHE_KEY,
    ttlMinutes = DEFAULT_TTL_MINUTES,
    enableBackgroundRefresh = true,
    // enableRealtime = false, // TODO: Implement realtime updates
    autoMigrate = true
  } = options;

  const [candidates, setCandidatesState] = useState<CandidateData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFromCache, setIsFromCache] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [migrationCompleted, setMigrationCompleted] = useState(false);

  const userId = user?.id;

  // Load from cache
  const loadFromCache = useCallback(async (): Promise<CandidateData[] | null> => {
    if (!userId) return null;

    try {
      const cachedData = await candidateCacheService.getCachedCandidates(userId, cacheKey);
      return cachedData;
    } catch (error) {
      console.error('❌ Error loading from cache:', error);
      setError(`Failed to load from cache: ${error}`);
      return null;
    }
  }, [userId, cacheKey]);

  // Save to cache
  const saveToCache = useCallback(async (candidatesToCache: CandidateData[]): Promise<void> => {
    if (!userId) return;

    try {
      await candidateCacheService.setCachedCandidates(
        userId,
        cacheKey,
        candidatesToCache,
        { ttlMinutes }
      );
    } catch (error) {
      console.error('❌ Error saving to cache:', error);
      setError(`Failed to save to cache: ${error}`);
    }
  }, [userId, cacheKey, ttlMinutes]);

  // Fetch fresh data
  const fetchFreshData = useCallback(async (): Promise<CandidateData[]> => {
    if (!userId) return [];

    console.log('🔄 Fetching fresh candidate data...');
    setIsLoading(true);
    setError(null);
    
    try {
      const freshCandidates = await fetchFunction();
      await saveToCache(freshCandidates);
      return freshCandidates;
    } catch (error) {
      console.error('❌ Error fetching fresh data:', error);
      setError(`Failed to fetch fresh data: ${error}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [userId, fetchFunction, saveToCache]);

  // Refresh cache manually
  const refreshCache = useCallback(async (): Promise<void> => {
    const freshCandidates = await fetchFreshData();
    setCandidatesState(freshCandidates);
    setIsFromCache(false);
    setError(null);
  }, [fetchFreshData]);

  // Clear cache
  const clearCache = useCallback(async (): Promise<void> => {
    if (!userId) return;

    try {
      await candidateCacheService.clearCache(userId, cacheKey);
      console.log('🗑️ Cache cleared');
    } catch (error) {
      console.error('❌ Error clearing cache:', error);
      setError(`Failed to clear cache: ${error}`);
    }
  }, [userId, cacheKey]);

  // Set candidates and update cache
  const setCandidates = useCallback(async (newCandidates: CandidateData[]): Promise<void> => {
    setCandidatesState(newCandidates);
    await saveToCache(newCandidates);
  }, [saveToCache]);

  // Invalidate cache by pattern
  const invalidatePattern = useCallback(async (pattern: string): Promise<number> => {
    if (!userId) return 0;

    try {
      const deletedCount = await candidateCacheService.invalidateByPattern(userId, pattern);
      return deletedCount;
    } catch (error) {
      console.error('❌ Error invalidating cache pattern:', error);
      setError(`Failed to invalidate cache pattern: ${error}`);
      return 0;
    }
  }, [userId]);

  // Get cache metrics
  const getCacheMetrics = useCallback(async (): Promise<CacheMetrics | null> => {
    if (!userId) return null;

    try {
      return await candidateCacheService.getCacheMetrics(userId, cacheKey);
    } catch (error) {
      console.error('❌ Error getting cache metrics:', error);
      setError(`Failed to get cache metrics: ${error}`);
      return null;
    }
  }, [userId, cacheKey]);

  // Get cache size
  const getCacheSize = useCallback(async (): Promise<{ totalEntries: number; totalSizeKB: number }> => {
    if (!userId) return { totalEntries: 0, totalSizeKB: 0 };

    try {
      return await candidateCacheService.getCacheSize(userId);
    } catch (error) {
      console.error('❌ Error getting cache size:', error);
      setError(`Failed to get cache size: ${error}`);
      return { totalEntries: 0, totalSizeKB: 0 };
    }
  }, [userId]);

  // Migrate legacy localStorage data
  const migrateLegacyData = useCallback(async (): Promise<{ migrated: number; errors: string[] }> => {
    if (!userId) return { migrated: 0, errors: [] };

    try {
      console.log('🔄 Starting legacy cache migration...');
      const result = await candidateCacheService.migrateLegacyCache(userId);
      
      if (result.migrated > 0) {
        console.log(`✅ Migrated ${result.migrated} legacy cache entries`);
        // Refresh cache after migration
        await refreshCache();
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error migrating legacy cache:', error);
      setError(`Failed to migrate legacy cache: ${error}`);
      return { migrated: 0, errors: [`Migration failed: ${error}`] };
    }
  }, [userId, refreshCache]);

  // Background refresh when cache is half-expired
  useEffect(() => {
    if (!enableBackgroundRefresh || !userId) return;

    const backgroundRefresh = async () => {
      try {
        const isValid = await candidateCacheService.isCacheValid(userId, cacheKey);
        if (!isValid) {
          // Cache is expired or missing, refresh in background
          console.log('🔄 Background refresh starting...');
          fetchFreshData().then(freshCandidates => {
            setCandidatesState(freshCandidates);
            setIsFromCache(false);
            console.log('✅ Background refresh completed');
          }).catch(error => {
            console.error('❌ Background refresh failed:', error);
          });
        }
      } catch (error) {
        console.error('❌ Background refresh check failed:', error);
      }
    };

    // Check every 5 minutes for background refresh
    const interval = setInterval(backgroundRefresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [enableBackgroundRefresh, userId, cacheKey, fetchFreshData]);

  // Initial load with automatic migration
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const initializeCache = async () => {
      try {
        // Auto-migrate legacy data if enabled
        if (autoMigrate && !migrationCompleted) {
          await migrateLegacyData();
          setMigrationCompleted(true);
        }

        // Try to load from cache first
        const cachedCandidates = await loadFromCache();
        
        if (cachedCandidates && cachedCandidates.length > 0) {
          // Load from cache immediately
          setCandidatesState(cachedCandidates);
          setIsFromCache(true);
          setIsLoading(false);
          console.log(`🎯 Loaded ${cachedCandidates.length} candidates from cache`);
        } else {
          // Fetch fresh data
          fetchFreshData().then(freshCandidates => {
            setCandidatesState(freshCandidates);
            setIsFromCache(false);
            console.log(`🔄 Loaded ${freshCandidates.length} candidates from fresh data`);
          }).catch(error => {
            console.error('❌ Failed to load candidates:', error);
            setCandidatesState([]);
            setIsFromCache(false);
            setError(`Failed to load candidates: ${error}`);
          });
        }
      } catch (error) {
        console.error('❌ Error initializing cache:', error);
        setError(`Failed to initialize cache: ${error}`);
        setIsLoading(false);
      }
    };

    initializeCache();
  }, [userId, autoMigrate, migrationCompleted, loadFromCache, fetchFreshData, migrateLegacyData]);

  // Clean up expired cache on unmount
  useEffect(() => {
    return () => {
      if (userId) {
        candidateCacheService.cleanExpiredCache().catch(error => {
          console.error('❌ Error cleaning expired cache on unmount:', error);
        });
      }
    };
  }, [userId]);

  return {
    candidates,
    isLoading,
    isFromCache,
    error,
    refreshCache,
    clearCache,
    setCandidates,
    invalidatePattern,
    getCacheMetrics,
    getCacheSize,
    migrateLegacyData
  };
}

// Export the hook as default as well for easier importing
export default useSupabaseCandidateCache; 